// A Snapshot plus its audio, published as a seed — the uploads API's three
// calls, from any host that can hash and PUT.
//
// Two front ends feed this: an Audiotool project captured in the tab or the
// extension (extension/src/audiotool/project-upload.ts), and a folder of audio
// files dropped on the web app (web-shared/src/lib/web-upload/). Both arrive
// with a Snapshot whose audio clips carry no keys yet and the bytes each clip
// plays; everything after that is the same:
//
//   prepareSeed      hash every clip (the blob pool's address), mix a master
//                    when the caller has none, encode the MP3 previews, and
//                    write the manifest;
//   openSeedUpload   POST /uploads — the server answers with a plan per file,
//                    and `exists: true` for bytes the pool already holds;
//   putUploadJob     send one object, one PUT. Steps, not a loop, so a caller
//                    can show progress per file and retry one that failed
//                    without re-sending the rest;
//   completeSeedUpload  POST /complete with the keys stamped onto the clips.
//
// Pure apart from the transport: the MP3 encoder (WASM) is passed in, so
// shared/ stays free of runtime dependencies.

import type {
  AbortUploadRequest,
  CompleteUploadRequest,
  CompleteUploadResponse,
  CreateUploadRequest,
  CreateUploadResponse,
  SeedOrigin,
  UploadFileManifest,
  UploadFilePlan,
} from "../api-types.js";
import { decodeAudio, renderMasterMixFromSegments, type MixSegment } from "../audio/master-mix.js";
import { encodeWaveform, peaksOf } from "../audio/waveform.js";
import { sha256Hex } from "../hash/sha256.js";
import { faderToGain } from "../mixer.js";
import type { Snapshot } from "../seed-history.js";

/** How this environment reaches Seedbed. */
export interface SeedbedTransport {
  /** POST JSON to the core API (`/uploads`, …) as the signed-in producer. Throws on a non-2xx. */
  postJson<T>(path: string, body: unknown): Promise<T>;
  /** PUT bytes to a presigned URL, with the headers the plan says are signed into it. */
  put(url: string, bytes: Uint8Array, label: string, headers?: Record<string, string>): Promise<void>;
}

/** The audio of one clip, addressed by its place in the Snapshot. */
export interface SeedClipBytes {
  trackIndex: number;
  clipIndex: number;
  bytes: Uint8Array;
}

/** MP3 renditions, one per original — best effort; a missing one is not fatal. */
export type PreviewEncoder = (
  originals: ReadonlyArray<{ hash: string; bytes: Uint8Array }>,
) => Promise<Array<{ previewOf: string; bytes: Uint8Array }>>;

export interface SeedMeta {
  title: string;
  bpm: number;
  key: string;
  /** The seed this is a new version or remix of; null for a new root. */
  parentSeedId: string | null;
  note: string | null;
  origin: SeedOrigin;
}

export interface SeedInput {
  meta: SeedMeta;
  /** Audio clips carry `audioKey: null`; the server stamps the keys at /complete. */
  snapshot: Snapshot;
  /** Audio for `snapshot.audioTracks[t].arrangementClips[c]`. */
  clips: SeedClipBytes[];
  /** Optional bounces for `snapshot.midiTracks[t].arrangementClips[c]`. */
  midiClips?: SeedClipBytes[];
  /** Audio for `snapshot.audioTracks[t].sessionClips[c]` (Session View clips). */
  sessionClips?: SeedClipBytes[];
  /** Clips whose audio the pool already holds, by key: no bytes, nothing to
   * send — only named at /complete, which takes the seed's reference to each
   * (without one the blob sweep would delete audio the seed plays). */
  pooled?: PooledClip[];
  /** The host's own master render; null ⇒ a leveled sum of the clips. */
  master: Uint8Array | null;
}

/** An audio clip that plays a pool object already stored (a library clip). */
export interface PooledClip {
  list: "arrangement" | "session";
  trackIndex: number;
  clipIndex: number;
  key: string;
}

interface Placement {
  trackIndex: number;
  clipIndex: number;
  hash: string;
}

export interface PreparedSeed {
  create: CreateUploadRequest;
  /** Distinct clip objects by hash. */
  objects: Map<string, Uint8Array>;
  placements: Placement[];
  midiPlacements: Placement[];
  sessionPlacements: Placement[];
  pooled: PooledClip[];
  master: { bytes: Uint8Array; hash: string } | null;
  previews: Array<{ previewOf: string; bytes: Uint8Array }>;
}

/** One object to send, as planned by the server. */
export interface UploadJob {
  /** Stable for the session: the object's key. */
  id: string;
  label: string;
  kind: "master" | "clip" | "preview";
  plan: UploadFilePlan;
  bytes: Uint8Array;
}

export interface SeedUploadSession {
  seedId: string;
  prepared: PreparedSeed;
  /** What still has to be sent — the pool's `exists` objects are not here. */
  jobs: UploadJob[];
  /** Objects the pool already held. */
  reused: number;
  masterKey: string | null;
  /** Clip hash → its key, for stamping. */
  keyByHash: Map<string, string>;
}

export type CompletedFile = CompleteUploadRequest["files"][number];

/** Hash, mix, encode and write the manifest. No network. */
export async function prepareSeed(
  input: SeedInput,
  encodePreviews: PreviewEncoder,
  stage?: (s: "mixing" | "encoding") => void,
): Promise<PreparedSeed> {
  const objects = new Map<string, Uint8Array>();
  const address = async (list: SeedClipBytes[]): Promise<Placement[]> => {
    const out: Placement[] = [];
    for (const clip of list) {
      const hash = await sha256Hex(clip.bytes);
      if (!objects.has(hash)) objects.set(hash, clip.bytes);
      out.push({ trackIndex: clip.trackIndex, clipIndex: clip.clipIndex, hash });
    }
    return out;
  };
  const placements = await address(input.clips);
  const midiPlacements = await address(input.midiClips ?? []);
  const sessionPlacements = await address(input.sessionClips ?? []);

  // ---- The master: the host's own render, or a leveled sum of the clips ----
  stage?.("mixing");
  const { snapshot } = input;
  const secondsPerBeat = 60 / (snapshot.song.tempo > 0 ? snapshot.song.tempo : 120);
  const segments: MixSegment[] = input.master ? [] : input.clips.flatMap((clip) => {
    const track = snapshot.audioTracks[clip.trackIndex];
    const info = track?.arrangementClips[clip.clipIndex];
    if (!track || !info || track.mute || info.muted) return [];
    return [{
      label: `${track.name} — ${info.name}`,
      bytes: clip.bytes,
      startSec: info.startTime * secondsPerBeat,
      // The snapshot carries Live's fader POSITION; the mix wants a gain.
      gain: faderToGain(track.volume),
    }];
  });
  const masterBytes = input.master ?? renderMasterMixFromSegments(segments)?.wav ?? null;
  const master = masterBytes ? { bytes: masterBytes, hash: await sha256Hex(masterBytes) } : null;

  // ---- Previews: the MP3 the hub's play button actually loads --------------
  stage?.("encoding");
  const previews = await encodePreviews([
    ...(master ? [{ hash: master.hash, bytes: master.bytes }] : []),
    ...[...objects].map(([hash, bytes]) => ({ hash, bytes })),
  ]);

  const originals = new Set(objects.keys());
  if (master) originals.add(master.hash);
  const files: UploadFileManifest[] = [
    ...(master ? [{ kind: "master" as const, hash: master.hash, sizeBytes: master.bytes.byteLength }] : []),
    ...[...objects].map(([hash, bytes]) => ({ kind: "clip" as const, hash, sizeBytes: bytes.byteLength })),
    ...previews
      .filter((p) => originals.has(p.previewOf))
      .map((p) => ({ kind: "preview" as const, previewOf: p.previewOf, sizeBytes: p.bytes.byteLength })),
  ];

  const { meta } = input;
  const create: CreateUploadRequest = {
    title: meta.title.trim() || "Untitled",
    bpm: Math.round(meta.bpm),
    key: meta.key,
    genre: null,
    description: null,
    parentSeedId: meta.parentSeedId,
    jamId: null,
    note: meta.note,
    changelog: [],
    arrangement: snapshot,
    files,
    origin: meta.origin,
  };
  return { create, objects, placements, midiPlacements, sessionPlacements, pooled: input.pooled ?? [], master, previews };
}

/** POST /uploads: the pending seed and what is left to send. */
export async function openSeedUpload(transport: SeedbedTransport, prepared: PreparedSeed): Promise<SeedUploadSession> {
  const session = await transport.postJson<CreateUploadResponse>("/uploads", prepared.create);

  const planByHash = new Map<string, UploadFilePlan>();
  const previewPlanByOriginal = new Map<string, UploadFilePlan>();
  let masterPlan: UploadFilePlan | null = null;
  for (const p of session.files) {
    if (p.kind === "preview" && p.previewOf) previewPlanByOriginal.set(p.previewOf, p);
    else if (p.hash) {
      planByHash.set(p.hash, p);
      if (p.kind === "master") masterPlan = p;
    }
  }

  const jobs: UploadJob[] = [];
  let reused = 0;
  // One job per plan. The mix of a lone 16-bit WAV is byte-identical to the
  // track, so master and clip share one plan; queued twice, a multipart
  // upload was completed twice and /complete failed.
  const queued = new Set<string>();
  const want = (plan: UploadFilePlan | undefined, bytes: Uint8Array, kind: UploadJob["kind"], label: string): void => {
    if (!plan || queued.has(plan.key)) return;
    queued.add(plan.key);
    if (plan.exists) reused++;
    else jobs.push({ id: plan.key, label, kind, plan, bytes });
  };
  const { master, objects, previews } = prepared;
  if (master && masterPlan) want(masterPlan, master.bytes, "master", "master");
  for (const [hash, bytes] of objects) want(planByHash.get(hash), bytes, "clip", `clip ${hash.slice(0, 8)}`);
  // A missing preview plan is not fatal: the seed is whole without previews,
  // they only make the hub faster.
  for (const p of previews) want(previewPlanByOriginal.get(p.previewOf), p.bytes, "preview", `preview ${p.previewOf.slice(0, 8)}`);

  const keyByHash = new Map<string, string>();
  for (const [hash, plan] of planByHash) keyByHash.set(hash, plan.key);
  return { seedId: session.seedId, prepared, jobs, reused, masterKey: masterPlan?.key ?? null, keyByHash };
}

/**
 * Send one planned object in one PUT, carrying the headers the server signed
 * into the URL (a pool object's checksum — R2 refuses any other bytes). A
 * failure re-sends the whole object: with no parts there is nothing to resume.
 */
export async function putUploadJob(
  transport: SeedbedTransport,
  job: Pick<UploadJob, "plan" | "bytes" | "label">,
  onProgress?: (sentBytes: number) => void,
): Promise<CompletedFile> {
  const { plan, bytes, label } = job;
  if (!plan.putUrl) throw new Error(`No upload URL for "${label}".`);
  await transport.put(plan.putUrl, bytes, label, plan.putHeaders);
  onProgress?.(bytes.byteLength);
  return { key: plan.key };
}

/** POST /complete: the server verifies every object and the seed goes live. */
export async function completeSeedUpload(
  transport: SeedbedTransport,
  session: SeedUploadSession,
  completed: CompletedFile[],
): Promise<CompleteUploadResponse> {
  const { prepared, keyByHash, masterKey } = session;
  const stamp = (list: Placement[]): Array<{ trackIndex: number; clipIndex: number; key: string }> =>
    list.flatMap((p) => {
      const key = keyByHash.get(p.hash);
      return key ? [{ trackIndex: p.trackIndex, clipIndex: p.clipIndex, key }] : [];
    });
  const pooledIn = (list: PooledClip["list"]): Array<{ trackIndex: number; clipIndex: number; key: string }> =>
    prepared.pooled.filter((p) => p.list === list).map(({ trackIndex, clipIndex, key }) => ({ trackIndex, clipIndex, key }));
  const complete: CompleteUploadRequest = {
    files: completed,
    // No stems: current seeds carry per-clip audio. Nothing bounces a
    // full-length track render here.
    stems: [],
    ...(masterKey ? { masterKey } : {}),
    clipAudio: [...stamp(prepared.placements), ...pooledIn("arrangement")],
    ...(prepared.midiPlacements.length > 0 ? { midiClipAudio: stamp(prepared.midiPlacements) } : {}),
    ...(prepared.sessionPlacements.length > 0 || pooledIn("session").length > 0
      ? { sessionClipAudio: [...stamp(prepared.sessionPlacements), ...pooledIn("session")] }
      : {}),
    ...(prepared.master ? waveformOf(prepared.master.bytes) : {}),
  };
  return transport.postJson<CompleteUploadResponse>(`/uploads/${encodeURIComponent(session.seedId)}/complete`, complete);
}

/** POST /abort: drop a pending seed the producer walked away from. Best effort. */
export async function abortSeedUpload(transport: SeedbedTransport, session: SeedUploadSession): Promise<void> {
  const planned = session.jobs.map((j) => j.plan);
  const body: AbortUploadRequest = {
    previews: planned.filter((p) => p.kind === "preview").map((p) => p.key),
  };
  try {
    await transport.postJson<unknown>(`/uploads/${encodeURIComponent(session.seedId)}/abort`, body);
  } catch {
    // The daily sweep removes pending seeds older than a day anyway.
  }
}

/** The master's peaks and length for the web app's waveform — nothing if it won't decode. */
function waveformOf(master: Uint8Array): { waveform: string; durationMs: number } | Record<string, never> {
  try {
    const decoded = decodeAudio(master);
    if (!decoded || decoded.sampleRate <= 0) return {};
    return {
      waveform: encodeWaveform(peaksOf(decoded)),
      durationMs: Math.round((decoded.frames / decoded.sampleRate) * 1000),
    };
  } catch {
    return {};
  }
}
